/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from '@fluidframework/common-utils';
import { isDetachedSequenceId } from './Identifiers';
import { fail } from './Common';
/**
 * Compresses a given {@link PlaceholderTree}
 * (Such as a {@link ChangeNode} or {@link BuildNode}) into an array,
 * while also string interning all node {@link Definition}s and {@link TraitLabel}s.
 * See {@link CompressedPlaceholderTree} for format.
 */
export class InterningTreeCompressor {
    compress(node, interner, idNormalizer) {
        this.previousId = undefined;
        return this.compressI(node, interner, idNormalizer);
    }
    compressI(node, interner, idNormalizer) {
        var _a, _b;
        if (isDetachedSequenceId(node)) {
            return node;
        }
        const internedDefinition = (_a = interner.getInternedId(node.definition)) !== null && _a !== void 0 ? _a : node.definition;
        const normalizedId = idNormalizer.normalizeToOpSpace(node.identifier);
        const compressedId = canElideId(this.previousId, normalizedId) ? undefined : normalizedId;
        this.previousId = normalizedId;
        const compressedTraits = [];
        // Omit traits if empty and payload is undefined.
        const traits = Object.entries(node.traits).sort();
        if (traits.length > 0 || node.payload !== undefined) {
            for (const [label, trait] of traits) {
                compressedTraits.push((_b = interner.getInternedId(label)) !== null && _b !== void 0 ? _b : label, trait.map((child) => this.compressI(child, interner, idNormalizer)));
            }
        }
        const payloadTraits = node.payload !== undefined ? [node.payload, ...compressedTraits] : compressedTraits;
        if (payloadTraits.length > 0) {
            if (compressedId !== undefined) {
                return [internedDefinition, compressedId, payloadTraits];
            }
            return [internedDefinition, payloadTraits];
        }
        if (compressedId !== undefined) {
            return [internedDefinition, compressedId];
        }
        return [internedDefinition];
    }
    decompress(node, interner, idNormalizer) {
        if (isDetachedSequenceId(node)) {
            return node;
        }
        const rootId = node[1];
        assert(typeof rootId === 'number', 0x63c /* Root node was compressed with no ID */);
        this.previousId = rootId;
        return this.decompressI(node, interner, idNormalizer);
    }
    decompressI(node, interner, idNormalizer) {
        var _a;
        if (isDetachedSequenceId(node)) {
            return node;
        }
        let compressedId;
        let compressedTraits;
        let payload;
        const [maybeInternedDefinition, idOrPayloadTraits, payloadTraits] = node;
        if (idOrPayloadTraits !== undefined) {
            if (typeof idOrPayloadTraits === 'number') {
                compressedId = idOrPayloadTraits;
                if (payloadTraits !== undefined) {
                    compressedTraits = payloadTraits;
                }
            }
            else {
                compressedTraits = idOrPayloadTraits;
            }
        }
        const definition = typeof maybeInternedDefinition === 'string'
            ? maybeInternedDefinition
            : interner.getString(maybeInternedDefinition);
        let identifier;
        if (compressedId !== undefined) {
            identifier = compressedId;
        }
        else {
            const prevId = (_a = this.previousId) !== null && _a !== void 0 ? _a : fail();
            identifier = prevId < 0 ? (prevId - 1) : (prevId + 1);
        }
        this.previousId = identifier;
        const traits = {};
        if (compressedTraits !== undefined) {
            let offset;
            if (compressedTraits.length % 2 === 1) {
                offset = 1;
                payload = compressedTraits[0];
            }
            else {
                offset = 0;
            }
            const traitsLength = compressedTraits.length - offset;
            for (let i = 0; i < traitsLength; i += 2) {
                const offsetIndex = i + offset;
                const maybeCompressedLabel = compressedTraits[offsetIndex];
                const compressedChildren = compressedTraits[offsetIndex + 1];
                const decompressedTraits = compressedChildren.map((child) => this.decompressI(child, interner, idNormalizer));
                const label = typeof maybeCompressedLabel === 'string'
                    ? maybeCompressedLabel
                    : interner.getString(maybeCompressedLabel);
                traits[label] = decompressedTraits;
            }
        }
        const decompressedNode = {
            identifier: idNormalizer.normalizeToSessionSpace(identifier),
            definition,
            traits,
        };
        if (payload !== undefined) {
            decompressedNode.payload = payload;
        }
        return decompressedNode;
    }
}
function canElideId(previousId, id) {
    if (previousId === undefined) {
        return false;
    }
    const numericId = previousId;
    if (numericId < 0) {
        return id === numericId - 1;
    }
    return id === numericId + 1;
}
//# sourceMappingURL=TreeCompressor.js.map